import React from 'react';
import { connect } from 'react-redux';
import {
  Grid, Box, Typography, Chip,
  Accordion, AccordionSummary, AccordionDetails,
  Switch, FormControlLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ButtonInc from '../../../shared-ui/ButtonInc';
import ModalInc from '../../../shared-ui/ModalInc';
import AlertDialog from '../../../components/AlertDialog/index.es6';
import SkeletonWrapper from '../../NewProductList/components/SkeletonWrapper/SkeletonWrapper';
import {
  getOrgClaimProcConfigsRequest,
  createOrgClaimProcConfigRequest,
  updateOrgClaimProcConfigRequest,
  deleteOrgClaimProcConfigRequest,
  resetCreateOrgClaimProcConfigError,
  resetUpdateOrgClaimProcConfigError,
  resetDeleteOrgClaimProcConfigError,
} from '../../OrganizationClaimProcessing/Redux/actions';
import { formatDateLocale } from '../../../lib/utils.es6';

var DEFAULT_STATUSES = [
  'all_docs_pending', 'decision_pending', 'approved', 'rejected', 'approved_paid',
];

var FIELD_TYPES = ['text', 'number', 'date', 'select', 'boolean'];
var BENEFIT_SOURCES = ['quotation', 'static', 'extension'];

function makeDefaultStatuses() {
  return DEFAULT_STATUSES.map(function(s) {
    return { code: s, label: s.replace(/_/g, ' ') };
  });
}

function makeEmptyForm() {
  return {
    code: '',
    label: '',
    product_type: '',
    benefit_calculation_source: 'quotation',
    active: true,
    statuses: makeDefaultStatuses(),
    actions_by_status: {},
    documents: [],
    claim_detail_fields: [],
  };
}

// ─── Inline style helpers ────────────────────────────────────────────────────

var inputStyle = {
  display: 'block',
  width: '100%',
  padding: '6px 8px',
  marginTop: 2,
  boxSizing: 'border-box',
  borderRadius: 4,
  border: '1px solid #D1D5DB',
  fontSize: 13,
  background: '#fff',
};

var labelStyle = {
  fontWeight: 600,
  fontSize: 12,
  color: '#374151',
  display: 'block',
  marginBottom: 2,
};

var sectionHeadingStyle = {
  fontWeight: 700,
  fontSize: 13,
  color: '#111827',
  marginBottom: 8,
  marginTop: 16,
  paddingBottom: 4,
  borderBottom: '2px solid #F59E0B',
  display: 'inline-block',
};

var rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 6,
  background: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: 4,
  padding: '6px 8px',
};

var removeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#EF4444',
  fontSize: 18,
  lineHeight: 1,
  padding: '0 4px',
  flexShrink: 0,
};

var addBtnStyle = {
  background: 'none',
  border: '1px dashed #9CA3AF',
  borderRadius: 4,
  color: '#6B7280',
  cursor: 'pointer',
  fontSize: 12,
  padding: '4px 10px',
  marginTop: 4,
};

// ─── Component ───────────────────────────────────────────────────────────────

class ClaimProcessingTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAddModal: false,
      showEditModal: false,
      showDeleteConfirm: false,
      selectedItem: null,
      form: makeEmptyForm(),
    };
    this.openAdd = this.openAdd.bind(this);
    this.openEdit = this.openEdit.bind(this);
    this.openDelete = this.openDelete.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.setField = this.setField.bind(this);

    // Status helpers
    this.onStatusDragEnd = this.onStatusDragEnd.bind(this);
    this.addCustomStatus = this.addCustomStatus.bind(this);

    // Action helpers
    this.addAction = this.addAction.bind(this);

    // Document helpers
    this.addDocument = this.addDocument.bind(this);

    // Detail field helpers
    this.addDetailField = this.addDetailField.bind(this);
  }

  componentWillMount() {
    this.props.dispatch(
      getOrgClaimProcConfigsRequest(this.props.organizationGuid)
    );
  }

  componentDidUpdate(prevProps) {
    var d = this.props.orgClaimProcData;
    var p = prevProps.orgClaimProcData;
    if (d.created && !p.created) {
      this.setState({ showAddModal: false, form: makeEmptyForm() });
    }
    if (d.updated && !p.updated) {
      this.setState({ showEditModal: false, selectedItem: null });
    }
    if (d.deleted && !p.deleted) {
      this.setState({ showDeleteConfirm: false, selectedItem: null });
    }
  }

  // ─── Open handlers ──────────────────────────────────────────────────────

  openAdd() {
    this.setState({ showAddModal: true, form: makeEmptyForm() });
  }

  openEdit(item) {
    var pd = item.claim_proc_data || {};

    var statuses = (pd.statuses && pd.statuses.length)
      ? pd.statuses
      : makeDefaultStatuses();

    var actions_by_status = pd.actions_by_status || {};
    var documents = pd.documents || [];
    var claim_detail_fields = pd.claim_detail_fields || [];

    this.setState({
      showEditModal: true,
      selectedItem: item,
      form: {
        code: item.code || '',
        label: pd.label || '',
        product_type: item.product_type || '',
        benefit_calculation_source: (pd.benefit_calculation && pd.benefit_calculation.source)
          || 'quotation',
        active: item.active !== false,
        statuses: statuses,
        actions_by_status: actions_by_status,
        documents: documents,
        claim_detail_fields: claim_detail_fields,
      },
    });
  }

  openDelete(item) {
    this.setState({ showDeleteConfirm: true, selectedItem: item });
  }

  // ─── Generic field setter ────────────────────────────────────────────────

  setField(name, value) {
    this.setState(function(s) {
      return { form: Object.assign({}, s.form, { [name]: value }) };
    });
  }

  // ─── Status helpers ──────────────────────────────────────────────────────

  onStatusDragEnd(result) {
    if (!result.destination) { return; }
    var statuses = this.state.form.statuses.slice();
    var moved = statuses.splice(result.source.index, 1)[0];
    statuses.splice(result.destination.index, 0, moved);
    this.setField('statuses', statuses);
  }

  addCustomStatus() {
    var statuses = this.state.form.statuses.slice();
    statuses.push({ code: '', label: '' });
    this.setField('statuses', statuses);
  }

  removeStatus(i) {
    var statuses = this.state.form.statuses.slice();
    statuses.splice(i, 1);
    this.setField('statuses', statuses);
  }

  updateStatusLabel(i, val) {
    var statuses = this.state.form.statuses.slice();
    statuses[i] = Object.assign({}, statuses[i], { label: val });
    this.setField('statuses', statuses);
  }

  updateStatusCode(i, val) {
    var statuses = this.state.form.statuses.slice();
    statuses[i] = Object.assign({}, statuses[i], { code: val });
    this.setField('statuses', statuses);
  }

  // ─── Action helpers ──────────────────────────────────────────────────────

  addAction(statusCode) {
    var abs = Object.assign({}, this.state.form.actions_by_status);
    var existing = (abs[statusCode] || []).slice();
    existing.push({ code: '', label: '', permission: '', transitions_to: '' });
    abs[statusCode] = existing;
    this.setField('actions_by_status', abs);
  }

  removeAction(statusCode, i) {
    var abs = Object.assign({}, this.state.form.actions_by_status);
    var existing = (abs[statusCode] || []).slice();
    existing.splice(i, 1);
    abs[statusCode] = existing;
    this.setField('actions_by_status', abs);
  }

  updateAction(statusCode, i, key, val) {
    var abs = Object.assign({}, this.state.form.actions_by_status);
    var existing = (abs[statusCode] || []).slice();
    existing[i] = Object.assign({}, existing[i], { [key]: val });
    abs[statusCode] = existing;
    this.setField('actions_by_status', abs);
  }

  // ─── Document helpers ────────────────────────────────────────────────────

  addDocument() {
    var docs = this.state.form.documents.slice();
    docs.push({ code: '', label: '', required: false, applies_to_status: '' });
    this.setField('documents', docs);
  }

  removeDocument(i) {
    var docs = this.state.form.documents.slice();
    docs.splice(i, 1);
    this.setField('documents', docs);
  }

  updateDocument(i, key, val) {
    var docs = this.state.form.documents.slice();
    docs[i] = Object.assign({}, docs[i], { [key]: val });
    this.setField('documents', docs);
  }

  // ─── Detail field helpers ─────────────────────────────────────────────────

  addDetailField() {
    var fields = this.state.form.claim_detail_fields.slice();
    fields.push({ name: '', label: '', field_type: 'text' });
    this.setField('claim_detail_fields', fields);
  }

  removeDetailField(i) {
    var fields = this.state.form.claim_detail_fields.slice();
    fields.splice(i, 1);
    this.setField('claim_detail_fields', fields);
  }

  updateDetailField(i, key, val) {
    var fields = this.state.form.claim_detail_fields.slice();
    fields[i] = Object.assign({}, fields[i], { [key]: val });
    this.setField('claim_detail_fields', fields);
  }

  // ─── Payload builder ─────────────────────────────────────────────────────

  buildPayload() {
    var f = this.state.form;
    return {
      organization_guid: this.props.organizationGuid,
      code: f.code,
      product_type: f.product_type,
      active: f.active,
      claim_proc_data: {
        label: f.label,
        statuses: f.statuses,
        actions_by_status: f.actions_by_status,
        documents: f.documents,
        claim_detail_fields: f.claim_detail_fields,
        benefit_calculation: { source: f.benefit_calculation_source },
      },
    };
  }

  handleCreate() {
    this.props.dispatch(createOrgClaimProcConfigRequest(this.buildPayload()));
  }

  handleUpdate() {
    var payload = Object.assign({}, this.buildPayload(), {
      guid: this.state.selectedItem.guid,
    });
    this.props.dispatch(updateOrgClaimProcConfigRequest(payload));
  }

  handleDelete() {
    this.props.dispatch(deleteOrgClaimProcConfigRequest({
      guid: this.state.selectedItem.guid,
      organization_guid: this.props.organizationGuid,
    }));
  }

  // ─── Section 1: Basic Info ────────────────────────────────────────────────

  renderBasicInfo() {
    var f = this.state.form;
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <label style={labelStyle}>Code *</label>
          <input
            style={inputStyle}
            value={f.code}
            onChange={function(e) { this.setField('code', e.target.value); }.bind(this)}
            placeholder="e.g. funeral_claim_proc"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <label style={labelStyle}>Label</label>
          <input
            style={inputStyle}
            value={f.label}
            onChange={function(e) { this.setField('label', e.target.value); }.bind(this)}
            placeholder="Funeral Claim Processing"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <label style={labelStyle}>Product Type</label>
          <input
            style={inputStyle}
            value={f.product_type}
            onChange={function(e) { this.setField('product_type', e.target.value); }.bind(this)}
            placeholder="funeral | motor | medical | device"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <label style={labelStyle}>Benefit Calculation Source</label>
          <select
            style={inputStyle}
            value={f.benefit_calculation_source}
            onChange={function(e) {
              this.setField('benefit_calculation_source', e.target.value);
            }.bind(this)}>
            {BENEFIT_SOURCES.map(function(s) {
              return <option key={s} value={s}>{s}</option>;
            })}
          </select>
        </Grid>
        <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
          <FormControlLabel
            control={
              <Switch
                checked={f.active}
                onChange={function(e) { this.setField('active', e.target.checked); }.bind(this)}
                size="small"
                color="success"
              />
            }
            label={<span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Active</span>}
          />
        </Grid>
      </Grid>
    );
  }

  // ─── Section 2: Claim Statuses ────────────────────────────────────────────

  renderStatuses() {
    var statuses = this.state.form.statuses;
    var self = this;

    return (
      <Box sx={{ mt: 2 }}>
        <span style={sectionHeadingStyle}>Claim Statuses (drag to reorder)</span>
        <DragDropContext onDragEnd={this.onStatusDragEnd}>
          <Droppable droppableId="statuses-droppable">
            {function(provided) {
              return (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{ minHeight: 8 }}
                >
                  {statuses.map(function(status, i) {
                    var isDefault = DEFAULT_STATUSES.indexOf(status.code) !== -1;
                    return (
                      <Draggable
                        key={'status-' + i}
                        draggableId={'status-' + i}
                        index={i}
                      >
                        {function(drag) {
                          return (
                            <div
                              ref={drag.innerRef}
                              {...drag.draggableProps}
                              style={Object.assign({}, rowStyle, drag.draggableProps.style)}
                            >
                              <span
                                {...drag.dragHandleProps}
                                style={{
                                  cursor: 'grab',
                                  color: '#9CA3AF',
                                  fontSize: 18,
                                  lineHeight: 1,
                                  flexShrink: 0,
                                  userSelect: 'none',
                                }}
                                title="Drag to reorder"
                              >
                                &#8942;&#8942;
                              </span>
                              {isDefault ? (
                                <Chip
                                  label={status.code}
                                  size="small"
                                  sx={{
                                    bgcolor: '#FFF7ED',
                                    color: '#C2410C',
                                    border: '1px solid #FDBA74',
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    flexShrink: 0,
                                    maxWidth: 180,
                                  }}
                                />
                              ) : (
                                <input
                                  style={Object.assign({}, inputStyle, {
                                    marginTop: 0,
                                    width: 160,
                                    flexShrink: 0,
                                  })}
                                  value={status.code}
                                  placeholder="status_code"
                                  onChange={function(e) {
                                    self.updateStatusCode(i, e.target.value);
                                  }}
                                />
                              )}
                              <input
                                style={Object.assign({}, inputStyle, {
                                  marginTop: 0,
                                  flex: 1,
                                })}
                                value={status.label}
                                placeholder="Label"
                                onChange={function(e) {
                                  self.updateStatusLabel(i, e.target.value);
                                }}
                              />
                              <button
                                style={removeBtn}
                                disabled={isDefault}
                                title={isDefault ? 'Default status — cannot remove' : 'Remove'}
                                onClick={function() { self.removeStatus(i); }}
                              >
                                &times;
                              </button>
                            </div>
                          );
                        }}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
        <button style={addBtnStyle} onClick={this.addCustomStatus}>
          + Add Custom Status
        </button>
      </Box>
    );
  }

  // ─── Section 3: Actions by Status ─────────────────────────────────────────

  renderActions() {
    var statuses = this.state.form.statuses;
    var abs = this.state.form.actions_by_status;
    var self = this;

    return (
      <Box sx={{ mt: 2 }}>
        <span style={sectionHeadingStyle}>Actions per Status</span>
        {statuses.map(function(status) {
          var sc = status.code;
          if (!sc) { return null; }
          var actions = abs[sc] || [];
          return (
            <Accordion
              key={sc}
              disableGutters
              sx={{
                mb: 0.5,
                border: '1px solid #E5E7EB',
                boxShadow: 'none',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                sx={{ minHeight: 36, py: 0, px: 1.5 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={sc}
                    size="small"
                    sx={{
                      bgcolor: '#FFF7ED',
                      color: '#C2410C',
                      border: '1px solid #FDBA74',
                      fontSize: 11,
                      fontFamily: 'monospace',
                    }}
                  />
                  <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                    {actions.length} action{actions.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, py: 1, bgcolor: '#FAFAFA' }}>
                {actions.map(function(action, ai) {
                  return (
                    <div key={ai} style={rowStyle}>
                      <input
                        style={Object.assign({}, inputStyle, { marginTop: 0, width: 110 })}
                        value={action.code}
                        placeholder="code"
                        onChange={function(e) {
                          self.updateAction(sc, ai, 'code', e.target.value);
                        }}
                      />
                      <input
                        style={Object.assign({}, inputStyle, { marginTop: 0, flex: 1 })}
                        value={action.label}
                        placeholder="Label"
                        onChange={function(e) {
                          self.updateAction(sc, ai, 'label', e.target.value);
                        }}
                      />
                      <input
                        style={Object.assign({}, inputStyle, { marginTop: 0, width: 130 })}
                        value={action.permission}
                        placeholder="PERMISSION"
                        onChange={function(e) {
                          self.updateAction(sc, ai, 'permission', e.target.value);
                        }}
                      />
                      <select
                        style={Object.assign({}, inputStyle, { marginTop: 0, width: 150 })}
                        value={action.transitions_to}
                        onChange={function(e) {
                          self.updateAction(sc, ai, 'transitions_to', e.target.value);
                        }}
                      >
                        <option value="">Transitions to…</option>
                        {statuses.map(function(st) {
                          if (!st.code) { return null; }
                          return (
                            <option key={st.code} value={st.code}>{st.code}</option>
                          );
                        })}
                      </select>
                      <button
                        style={removeBtn}
                        onClick={function() { self.removeAction(sc, ai); }}
                      >
                        &times;
                      </button>
                    </div>
                  );
                })}
                <button
                  style={addBtnStyle}
                  onClick={function() { self.addAction(sc); }}
                >
                  + Add Action
                </button>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  }

  // ─── Section 4: Documents ─────────────────────────────────────────────────

  renderDocuments() {
    var docs = this.state.form.documents;
    var statuses = this.state.form.statuses;
    var self = this;

    return (
      <Box sx={{ mt: 2 }}>
        <span style={sectionHeadingStyle}>Required Documents</span>
        {docs.map(function(doc, i) {
          return (
            <div key={i} style={rowStyle}>
              <input
                style={Object.assign({}, inputStyle, { marginTop: 0, width: 110 })}
                value={doc.code}
                placeholder="code"
                onChange={function(e) { self.updateDocument(i, 'code', e.target.value); }}
              />
              <input
                style={Object.assign({}, inputStyle, { marginTop: 0, flex: 1 })}
                value={doc.label}
                placeholder="Label"
                onChange={function(e) { self.updateDocument(i, 'label', e.target.value); }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!doc.required}
                    onChange={function(e) {
                      self.updateDocument(i, 'required', e.target.checked);
                    }}
                    size="small"
                    color="warning"
                  />
                }
                label={<span style={{ fontSize: 12, color: '#374151' }}>Required</span>}
                sx={{ mx: 0, flexShrink: 0 }}
              />
              <select
                style={Object.assign({}, inputStyle, { marginTop: 0, width: 160 })}
                value={doc.applies_to_status || ''}
                onChange={function(e) {
                  self.updateDocument(i, 'applies_to_status', e.target.value);
                }}
              >
                <option value="">Applies to (all)</option>
                {statuses.map(function(st) {
                  if (!st.code) { return null; }
                  return (
                    <option key={st.code} value={st.code}>{st.code}</option>
                  );
                })}
              </select>
              <button style={removeBtn} onClick={function() { self.removeDocument(i); }}>
                &times;
              </button>
            </div>
          );
        })}
        <button style={addBtnStyle} onClick={this.addDocument}>
          + Add Document
        </button>
      </Box>
    );
  }

  // ─── Section 5: Claim Detail Fields ──────────────────────────────────────

  renderDetailFields() {
    var fields = this.state.form.claim_detail_fields;
    var self = this;

    return (
      <Box sx={{ mt: 2 }}>
        <span style={sectionHeadingStyle}>Claim Detail Fields</span>
        {fields.map(function(field, i) {
          return (
            <div key={i} style={rowStyle}>
              <input
                style={Object.assign({}, inputStyle, { marginTop: 0, width: 120 })}
                value={field.name}
                placeholder="name"
                onChange={function(e) { self.updateDetailField(i, 'name', e.target.value); }}
              />
              <input
                style={Object.assign({}, inputStyle, { marginTop: 0, flex: 1 })}
                value={field.label}
                placeholder="Label"
                onChange={function(e) { self.updateDetailField(i, 'label', e.target.value); }}
              />
              <select
                style={Object.assign({}, inputStyle, { marginTop: 0, width: 100 })}
                value={field.field_type || 'text'}
                onChange={function(e) {
                  self.updateDetailField(i, 'field_type', e.target.value);
                }}
              >
                {FIELD_TYPES.map(function(t) {
                  return <option key={t} value={t}>{t}</option>;
                })}
              </select>
              <button style={removeBtn} onClick={function() { self.removeDetailField(i); }}>
                &times;
              </button>
            </div>
          );
        })}
        <button style={addBtnStyle} onClick={this.addDetailField}>
          + Add Field
        </button>
      </Box>
    );
  }

  // ─── Full form ────────────────────────────────────────────────────────────

  renderFormFields() {
    return (
      <Box sx={{ pb: 2 }}>
        {this.renderBasicInfo()}
        {this.renderStatuses()}
        {this.renderActions()}
        {this.renderDocuments()}
        {this.renderDetailFields()}
      </Box>
    );
  }

  // ─── Card ─────────────────────────────────────────────────────────────────

  renderCard(item) {
    var pd = item.claim_proc_data || {};
    var statuses = pd.statuses || [];
    return (
      <Grid item xs={12} sm={6} md={4} key={item.guid}>
        <Box sx={{
          border: '1px solid #E5E7EB',
          borderRadius: 2,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{
            px: 2.5, pt: 2.5, pb: 1.5,
            borderBottom: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <Box>
              <Typography variant="subtitle2"
                sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                {pd.label || item.code}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Typography variant="body2"
                  sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>
                  {item.code}
                </Typography>
                {item.product_type && (
                  <Chip label={item.product_type} size="small"
                    sx={{
                      height: 16, fontSize: 10,
                      bgcolor: '#FFF7ED', color: '#C2410C',
                      border: '1px solid #FDBA74',
                    }} />
                )}
              </Box>
            </Box>
            <Chip
              label={item.active !== false ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                height: 20, fontSize: 11, fontWeight: 600,
                bgcolor: item.active !== false ? '#F0FDF4' : '#FEF2F2',
                color: item.active !== false ? '#15803D' : '#B91C1C',
                border: item.active !== false ? '1px solid #BBF7D0' : '1px solid #FECACA',
              }}
            />
          </Box>
          <Box sx={{ px: 2.5, py: 1.5, flex: 1 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: '#6B7280', mb: 1 }}>
              {statuses.length} status bucket{statuses.length !== 1 ? 's' : ''}
            </Typography>
            {pd.benefit_calculation && (
              <Typography variant="body2" sx={{ fontSize: 11, color: '#9CA3AF' }}>
                Benefit: {pd.benefit_calculation.source || 'quotation'}
              </Typography>
            )}
            <Typography variant="body2" sx={{ fontSize: 11, color: '#9CA3AF', mt: 0.5 }}>
              Created: {item.created_at ? formatDateLocale(item.created_at) : '—'}
            </Typography>
          </Box>
          <Box sx={{
            px: 2.5, py: 1.5,
            borderTop: '1px solid #F3F4F6',
            display: 'flex',
            gap: 1,
          }}>
            <ButtonInc
              btnType="secondary"
              onClick={function() { this.openEdit(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12 }}
            >
              Edit
            </ButtonInc>
            <ButtonInc
              btnType="secondary"
              onClick={function() { this.openDelete(item); }.bind(this)}
              style={{ flex: 1, fontSize: 12, color: '#B91C1C', borderColor: '#FECACA' }}
            >
              Delete
            </ButtonInc>
          </Box>
        </Box>
      </Grid>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  render() {
    var d = this.props.orgClaimProcData;
    var s = this.state;
    var list = d.list || [];

    if (d.loading) { return <SkeletonWrapper loading={true} />; }

    return (
      <div>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}>
          <Box>
            <Typography variant="h6"
              sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
              Claim Processing Configs
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, color: '#6B7280', mt: 0.25 }}>
              Configure status machines, actions, and documents per product type.
            </Typography>
          </Box>
          <ButtonInc variant="contained" onClick={this.openAdd}>
            + Add Config
          </ButtonInc>
        </Box>

        {list.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#9CA3AF' }}>
            <Typography variant="body1" sx={{ fontSize: 15 }}>
              No claim processing configs yet.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 13, mt: 0.5 }}>
              Add one per product type (funeral, motor, medical, device).
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {list.map(this.renderCard.bind(this))}
          </Grid>
        )}

        <ModalInc
          modalTitle="Add Claim Processing Config"
          open={s.showAddModal}
          close={function() { this.setState({ showAddModal: false }); }.bind(this)}
          fullWidth
          maxWidth="lg"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showAddModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.creating}
              onClick={this.handleCreate}>
              Save
            </ButtonInc>,
          ]}
        >
          {this.renderFormFields()}
        </ModalInc>

        <ModalInc
          modalTitle="Edit Claim Processing Config"
          open={s.showEditModal}
          close={function() { this.setState({ showEditModal: false }); }.bind(this)}
          fullWidth
          maxWidth="lg"
          modalButtons={[
            <ButtonInc key="cancel" btnType="secondary"
              onClick={function() { this.setState({ showEditModal: false }); }.bind(this)}>
              Cancel
            </ButtonInc>,
            <ButtonInc key="save" variant="contained" loading={d.updating}
              onClick={this.handleUpdate}>
              Save
            </ButtonInc>,
          ]}
        >
          {this.renderFormFields()}
        </ModalInc>

        {s.showDeleteConfirm && (
          <AlertDialog
            show={s.showDeleteConfirm}
            title="Delete Config"
            type="warning"
            showConfirm
            confirmBtnText="Delete"
            showCancel
            onConfirm={this.handleDelete}
            onCancel={function() {
              this.setState({ showDeleteConfirm: false, selectedItem: null });
            }.bind(this)}
          >
            Delete <strong>{s.selectedItem && s.selectedItem.code}</strong>?
          </AlertDialog>
        )}

        {d.createError && (
          <AlertDialog
            show={!!d.createError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetCreateOrgClaimProcConfigError());
            }.bind(this)}
          >
            {d.createError.message || JSON.stringify(d.createError)}
          </AlertDialog>
        )}
        {d.updateError && (
          <AlertDialog
            show={!!d.updateError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetUpdateOrgClaimProcConfigError());
            }.bind(this)}
          >
            {d.updateError.message || JSON.stringify(d.updateError)}
          </AlertDialog>
        )}
        {d.deleteError && (
          <AlertDialog
            show={!!d.deleteError}
            title="Error"
            type="danger"
            showConfirm
            confirmBtnText="OK"
            onConfirm={function() {
              this.props.dispatch(resetDeleteOrgClaimProcConfigError());
            }.bind(this)}
          >
            {d.deleteError.message || JSON.stringify(d.deleteError)}
          </AlertDialog>
        )}
      </div>
    );
  }
}

export default connect(function(state) {
  return { orgClaimProcData: state.orgClaimProcConfigsData };
})(ClaimProcessingTab);
